import { useRef, useState, type FormEvent } from "react";
import { Upload } from "lucide-react";
import { Banner, Button, Field, Input, TextArea } from "@/shared/rd";
import { NamedCheckbox } from "@/shared/stores/components/rd-local";
import { PanelDrawer } from "@/shared/stores/components/drawer-panel";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  capturePrivateSession,
  isPrivateSessionCurrent,
} from "@/lib/api/private-session";
import type { AdminStoreDetailsInput, StoreView } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import {
  uploadStoreLogo,
  useUpdateStoreDetailsMutation,
} from "@/modules/stores/services/storeControls";

/** The backend's `STORE_SLUG_PATTERN`, mirrored so a typo is caught before the round trip. */
export const STORE_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9]|-(?=[a-z0-9])){2,39}$/;

type Props = {
  open: boolean;
  onClose: () => void;
  store: StoreView;
  /** Active restaurant tables: each is a printed QR sticker with the slug in it. */
  qrTableCount: number;
  /** Shown where the drawer was opened from, after a save. */
  onSaved?: (message: string) => void;
};

/**
 * The admin's store details edit: name, web address (slug), descriptions and
 * logo. Changing the slug is a rename with real consequences — the old link
 * dies at once, there is no redirect, staff are signed out and printed table
 * QR stickers stop working — so the warning names them and the save waits for
 * an explicit acknowledgement. `storeKind` is not editable anywhere.
 */
export function StoreDetailsEditModal({ open, onClose, store, qrTableCount, onSaved }: Props) {
  const t = useTranslations();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const lifetime = useRef(capturePrivateSession()).current;
  const mutation = useUpdateStoreDetailsMutation();
  const [name, setName] = useState(store.storeName);
  const [slug, setSlug] = useState(store.storeSlug);
  const [descriptionAr, setDescriptionAr] = useState(store.description ?? "");
  const [descriptionEn, setDescriptionEn] = useState(store.descriptionEn ?? "");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);

  const normalizedSlug = slug.trim().toLowerCase();
  const slugChanged = normalizedSlug !== store.storeSlug;
  const slugValid = STORE_SLUG_PATTERN.test(normalizedSlug);
  const busy = mutation.isPending || uploading;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSlugError(null);
    if (slugChanged && !slugValid) {
      setSlugError(t(keys.storeControls.slugInvalid));
      return;
    }
    const input: AdminStoreDetailsInput = {};
    if (name.trim() !== store.storeName) input.storeName = name.trim();
    if (slugChanged) input.storeSlug = normalizedSlug;
    if (descriptionAr.trim() !== (store.description ?? "")) {
      input.description = descriptionAr.trim() || null;
    }
    if (descriptionEn.trim() !== (store.descriptionEn ?? "")) {
      input.descriptionEn = descriptionEn.trim() || null;
    }
    try {
      if (logoFile) {
        setUploading(true);
        try {
          input.logoObjectKey = await uploadStoreLogo(store.id, logoFile);
        } catch {
          if (isPrivateSessionCurrent(lifetime)) {
            setError(t(keys.storeControls.logoUploadFailed));
          }
          return;
        } finally {
          setUploading(false);
        }
      } else if (removeLogo) {
        input.logoObjectKey = null;
      }
      if (Object.keys(input).length === 0) {
        onClose();
        return;
      }
      const result = await mutation.mutateAsync({ storeId: store.id, input });
      if (!isPrivateSessionCurrent(lifetime)) return;
      onSaved?.(
        result.revalidated
          ? t(keys.storeControls.saved)
          : t(keys.storeControls.appliedDelayed),
      );
      onClose();
    } catch (caught) {
      if (!isPrivateSessionCurrent(lifetime)) return;
      if (caught instanceof ApiJsonClientError && caught.code === "STORE_SLUG_TAKEN") {
        setSlugError(t(keys.storeControls.slugTaken));
        return;
      }
      setError(
        caught instanceof ApiJsonClientError && caught.message
          ? caught.message
          : t(keys.errors.generic),
      );
    }
  };

  const formId = `store-edit-${store.id}`;

  return (
    <PanelDrawer
      open={open}
      onClose={busy ? undefined : onClose}
      closeLabel={t(keys.common.close)}
      badge={store.storeKind === "RESTAURANT" ? "servicebell" : "cart-world"}
      title={t(keys.storeControls.editTitle)}
      sub={<span dir="ltr">{store.storeSlug}</span>}
      width={520}
      actions={
        <>
          <Button variant="ghost" muted size="sm" onClick={onClose} disabled={busy}>
            {t(keys.common.cancel)}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            size="sm"
            loading={busy}
            loadingLabel={t(uploading ? "admin.busyUploading" : "admin.saving")}
            disabled={name.trim().length < 2 || (slugChanged && !acknowledged)}
          >
            {t(keys.common.save)}
          </Button>
        </>
      }
    >
      <form id={formId} style={{ display: "flex", flexDirection: "column", gap: 14 }} onSubmit={submit}>
        {error ? (
          <Banner tone="error" title={t("storeControls.saveFailedTitle")}>
            {error} {t("admin.nothingChanged")}
          </Banner>
        ) : null}
        <Field label={t(keys.storeControls.name)} htmlFor={`${formId}-name`}>
          <Input
            id={`${formId}-name`}
            name="storeName"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field
          label={t(keys.storeControls.slug)}
          htmlFor={`${formId}-slug`}
          help={t(keys.storeControls.slugHint)}
          error={slugError ?? undefined}
        >
          <Input
            id={`${formId}-slug`}
            name="storeSlug"
            dir="ltr"
            invalid={Boolean(slugError)}
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
            value={slug}
            maxLength={40}
            autoCapitalize="none"
            spellCheck={false}
            onChange={(e) => {
              setSlug(e.target.value);
              setAcknowledged(false);
              setSlugError(null);
            }}
            onBlur={() => {
              if (slugChanged && !slugValid) setSlugError(t(keys.storeControls.slugInvalid));
            }}
          />
        </Field>

        {slugChanged ? (
          <Banner tone="warning">
            <span style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span>{t(keys.storeControls.slugWarning)}</span>
              {qrTableCount > 0 ? (
                <strong>{t(keys.storeControls.slugWarningTables, { count: qrTableCount })}</strong>
              ) : null}
              <NamedCheckbox name="slugAcknowledged" checked={acknowledged} onChange={setAcknowledged}>
                {t(keys.storeControls.slugWarningConfirm)}
              </NamedCheckbox>
            </span>
          </Banner>
        ) : null}

        <Field label={t(keys.storeControls.descriptionAr)} htmlFor={`${formId}-desc-ar`}>
          <TextArea
            id={`${formId}-desc-ar`}
            name="descriptionAr"
            dir="rtl"
            rows={3}
            maxLength={2000}
            value={descriptionAr}
            onChange={(e) => setDescriptionAr(e.target.value)}
          />
        </Field>
        <Field label={t(keys.storeControls.descriptionEn)} htmlFor={`${formId}-desc-en`}>
          <TextArea
            id={`${formId}-desc-en`}
            name="descriptionEn"
            dir="ltr"
            rows={3}
            maxLength={2000}
            value={descriptionEn}
            onChange={(e) => setDescriptionEn(e.target.value)}
          />
        </Field>

        <Field label={t(keys.storeControls.logo)}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {/* eslint-disable-next-line no-restricted-syntax -- a hidden file picker behind an RD button; the kit has no file control. */}
            <input
              ref={fileInput}
              type="file"
              name="logo"
              accept="image/png,image/jpeg,image/webp"
              disabled={busy}
              hidden
              onChange={(e) => {
                setLogoFile(e.target.files?.[0] ?? null);
                setRemoveLogo(false);
              }}
            />
            <Button size="sm" icon={Upload} disabled={busy} onClick={() => fileInput.current?.click()}>
              {t(keys.storeControls.logoChoose)}
            </Button>
            {logoFile ? (
              <span dir="auto" style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
                {logoFile.name}
              </span>
            ) : null}
          </div>
        </Field>
        {store.logoUrl && !logoFile ? (
          <NamedCheckbox name="removeLogo" checked={removeLogo} onChange={setRemoveLogo}>
            {t(keys.storeControls.logoRemove)}
          </NamedCheckbox>
        ) : null}
      </form>
    </PanelDrawer>
  );
}
